import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import InputSelect from '../../../components/InputSelect';
import SelectAdmin from '../../../components/selects/SelectAdmin';
import { createOptions } from '../../../helpers/selects';
import { convertToMoney } from '../../../helpers/masks';
import { convertMoenyToInt } from '../../../helpers/conversions';
import { RuleSentence } from './RuleSentence';
import {
    TRIGGERS,
    actionForTrigger,
    ACTIONS,
    originOptions,
    amountOperatorOptions,
    taskTypeOptions,
    taskDueOptions,
    cardModeOptions,
    responsibleModeOptions,
    CONDITION_TYPES,
} from '../../../seeds/crm/automations';

const stagesOfPipeline = (pipelines, pipelineId) => {
    const p = (pipelines || []).find((x) => String(x.id) === String(pipelineId));
    const stages = p?.stages ?? [];
    return [...stages].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
};

export const RuleEditorModal = ({ show, close, onSubmit, rule, initialTrigger, pipelines }) => {
    const isEdit = !!rule;
    const trigger = rule?.trigger ?? initialTrigger;
    const action = trigger ? actionForTrigger(trigger) : null;

    const [pipelineId, setPipelineId] = useState(null);
    const [stageId, setStageId] = useState(null);
    const [conditions, setConditions] = useState([]);
    const [cardMode, setCardMode] = useState('per_product');
    const [responsibleMode, setResponsibleMode] = useState('order_admin');
    const [responsibleAdminId, setResponsibleAdminId] = useState(null);
    const [taskType, setTaskType] = useState('call');
    const [taskTitle, setTaskTitle] = useState('');
    const [taskDue, setTaskDue] = useState(0);
    const [saving, setSaving] = useState(false);

    // (Re)inicializa os campos quando o modal abre.
    useEffect(() => {
        if (!show) return;
        const cfg = rule?.action_config ?? {};
        setPipelineId(rule?.pipeline_id ?? null);
        setStageId(rule?.pipeline_stage_id ?? null);
        setConditions(Array.isArray(rule?.conditions) ? rule.conditions.map((c) => ({ ...c })) : []);
        setCardMode(cfg.card_creation_mode ?? 'per_product');
        setResponsibleMode(cfg.card_responsible_mode ?? 'order_admin');
        setResponsibleAdminId(cfg.card_responsible_admin_id ?? null);
        setTaskType(cfg.task_type ?? 'call');
        setTaskTitle(cfg.task_title ?? '');
        setTaskDue(cfg.task_due_offset_days ?? 0);
    }, [show, rule, initialTrigger]);

    const pipelineOptions = useMemo(() => createOptions(pipelines, 'name', 'id'), [pipelines]);
    const stages = useMemo(() => stagesOfPipeline(pipelines, pipelineId), [pipelines, pipelineId]);
    const stageOptions = useMemo(() => createOptions(stages, 'name', 'id'), [stages]);

    // Tipos de condição disponíveis por gatilho (pedido é sempre "sistema": sem origem).
    const availableConditionTypes = useMemo(() => {
        if (trigger === TRIGGERS.BUDGET_CREATED) return [CONDITION_TYPES.ORIGIN, CONDITION_TYPES.AMOUNT];
        if (trigger === TRIGGERS.ORDER_CREATED) return [CONDITION_TYPES.AMOUNT];
        return [];
    }, [trigger]);

    const usedConditionTypes = conditions.map((c) => c.type);
    const addableTypes = availableConditionTypes.filter((t) => !usedConditionTypes.includes(t));

    const handlePipelineChange = (value) => {
        setPipelineId(value || null);
        setStageId(null);
    };

    const addCondition = (type) => {
        const base = type === CONDITION_TYPES.AMOUNT
            ? { type, operator: 'gte', value: 0 }
            : { type, value: 'site' };
        setConditions((prev) => [...prev, base]);
    };

    const updateCondition = (index, patch) =>
        setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));

    const removeCondition = (index) =>
        setConditions((prev) => prev.filter((_, i) => i !== index));

    const stageSemEtapas = !!pipelineId && stages.length === 0;

    const previewRule = {
        trigger,
        action,
        conditions,
        action_config:
            action === ACTIONS.CREATE_TASK
                ? { task_type: taskType, task_title: taskTitle, task_due_offset_days: taskDue }
                : { card_creation_mode: cardMode, card_responsible_mode: responsibleMode, card_responsible_admin_id: responsibleAdminId },
        pipeline: pipelines.find((p) => String(p.id) === String(pipelineId)) || null,
        stage: stages.find((s) => String(s.id) === String(stageId)) || null,
    };

    const valid =
        !!pipelineId &&
        !!stageId &&
        !stageSemEtapas &&
        (action !== ACTIONS.CREATE_TASK || !!taskType) &&
        !(trigger === TRIGGERS.ORDER_CREATED && responsibleMode === 'fixed_admin' && !responsibleAdminId);

    const saveHint = !valid
        ? !pipelineId ? 'Escolha um painel para continuar.'
        : !stageId ? 'Escolha a coluna de destino.'
        : stageSemEtapas ? null // já tem aviso próprio
        : action === ACTIONS.CREATE_TASK && !taskType ? 'Escolha o tipo de tarefa.'
        : trigger === TRIGGERS.ORDER_CREATED && responsibleMode === 'fixed_admin' && !responsibleAdminId ? 'Escolha o responsável fixo.'
        : null
        : null;

    const buildBody = () => {
        const body = {
            trigger,
            pipeline_id: pipelineId,
            pipeline_stage_id: stageId,
            conditions: conditions.length ? conditions : null,
        };

        if (action === ACTIONS.CREATE_TASK) {
            body.action_config = {
                task_type: taskType,
                task_title: taskTitle || null,
                task_due_offset_days: Number(taskDue),
            };
        } else if (trigger === TRIGGERS.ORDER_CREATED) {
            body.action_config = {
                card_creation_mode: cardMode,
                card_responsible_mode: responsibleMode,
                card_responsible_admin_id: responsibleMode === 'fixed_admin' ? responsibleAdminId : null,
            };
        } else {
            body.action_config = null;
        }

        return body;
    };

    const handleSubmit = () => {
        if (!valid) return;
        setSaving(true);
        Promise.resolve(onSubmit(buildBody()))
            .then(() => close())
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    if (!trigger) return null;

    return (
        <Modal
            title={isEdit ? 'Editar automação' : 'Nova automação'}
            show={show}
            close={close}
            footer={
                <>
                    {saveHint && <p className="rule-editor__save-hint">{saveHint}</p>}
                    <Button
                        type="primary"
                        size="small"
                        svg="save-white"
                        text="Salvar automação"
                        action={handleSubmit}
                        loading={saving}
                        disabled={!valid}
                    />
                </>
            }
        >
            <div className="rule-editor">
                <div className="rule-editor__preview" aria-live="polite">
                    <RuleSentence rule={previewRule} />
                </div>

                {availableConditionTypes.length > 0 && (
                    <div className="section">
                        <h4 className="rule-editor__section-title">Condições (opcional)</h4>

                        {conditions.length === 0 && (
                            <p className="rule-editor__hint">Sem condições: a regra vale para todos.</p>
                        )}

                        {conditions.map((cond, index) => (
                            <div className="rule-editor__condition" key={`${cond.type}-${index}`}>
                                {cond.type === CONDITION_TYPES.ORIGIN ? (
                                    <div className="rule-editor__condition-field">
                                        <InputSelect
                                            label="Origem"
                                            value={cond.value}
                                            options={originOptions}
                                            change={(value) => updateCondition(index, { value })}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="rule-editor__condition-field">
                                            <InputSelect
                                                label="Valor"
                                                value={cond.operator}
                                                options={amountOperatorOptions}
                                                change={(value) => updateCondition(index, { operator: value })}
                                            />
                                        </div>
                                        <div className="rule-editor__condition-field">
                                            <Input
                                                label="&nbsp;"
                                                value={convertToMoney(String(cond.value ?? 0))}
                                                change={(value) => updateCondition(index, { value: convertMoenyToInt(value) })}
                                            />
                                        </div>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="rule-editor__remove"
                                    aria-label="Remover condição"
                                    onClick={() => removeCondition(index)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}

                        {addableTypes.length > 0 ? (
                            <div className="rule-editor__add-conditions">
                                {addableTypes.includes(CONDITION_TYPES.ORIGIN) && (
                                    <Button type="link" size="small" text="+ Por origem" action={() => addCondition(CONDITION_TYPES.ORIGIN)} />
                                )}
                                {addableTypes.includes(CONDITION_TYPES.AMOUNT) && (
                                    <Button type="link" size="small" text="+ Por valor" action={() => addCondition(CONDITION_TYPES.AMOUNT)} />
                                )}
                            </div>
                        ) : (
                            <p className="rule-editor__hint">Você já adicionou as condições disponíveis.</p>
                        )}
                    </div>
                )}

                <div className="section">
                    <h4 className="rule-editor__section-title">
                        {action === ACTIONS.CREATE_TASK ? 'Onde e o que criar' : 'Onde criar o card'}
                    </h4>

                    <div className="row">
                        <div className="col-6">
                            <InputSelect
                                label="Painel"
                                value={pipelineId}
                                options={pipelineOptions}
                                change={handlePipelineChange}
                            />
                        </div>
                        <div className="col-6">
                            <InputSelect
                                label="Coluna"
                                value={stageId}
                                options={stageOptions}
                                change={(value) => setStageId(value || null)}
                                isDisabled={!pipelineId}
                            />
                        </div>
                    </div>

                    {stageSemEtapas && (
                        <p className="rule-editor__hint rule-editor__hint--warning">
                            Este painel ainda não tem colunas. Crie colunas no painel antes.
                        </p>
                    )}

                    {trigger === TRIGGERS.ORDER_CREATED && (
                        <div className="row">
                            <div className="col-6">
                                <InputSelect
                                    label="Como criar o card"
                                    value={cardMode}
                                    options={cardModeOptions}
                                    change={(value) => setCardMode(value || 'per_product')}
                                />
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    label="Responsável"
                                    value={responsibleMode}
                                    options={responsibleModeOptions}
                                    change={(value) => {
                                        setResponsibleMode(value || 'order_admin');
                                        if (value !== 'fixed_admin') setResponsibleAdminId(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {trigger === TRIGGERS.ORDER_CREATED && responsibleMode === 'fixed_admin' && (
                        <div className="row">
                            <div className="col-12">
                                <SelectAdmin
                                    selectedAdmin={responsibleAdminId}
                                    setSelectedAdmin={setResponsibleAdminId}
                                />
                            </div>
                        </div>
                    )}

                    {action === ACTIONS.CREATE_TASK && (
                        <>
                            <div className="row">
                                <div className="col-6">
                                    <InputSelect
                                        label="Tipo de tarefa"
                                        value={taskType}
                                        options={taskTypeOptions}
                                        change={(value) => setTaskType(value || 'call')}
                                    />
                                </div>
                                <div className="col-6">
                                    <InputSelect
                                        label="Prazo"
                                        value={taskDue}
                                        options={taskDueOptions}
                                        change={(value) => setTaskDue(value ?? 0)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <Input
                                        label="Título da tarefa (opcional)"
                                        value={taskTitle}
                                        change={setTaskTitle}
                                        placeholder="Ex.: Ligar para confirmar o prazo"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default RuleEditorModal;
